import { useState } from 'react';
import { User } from '../App';
import { ClientView } from './ClientDashboard';
import { Button } from './ui/button';
import TRNBranchSwitcher from './TRNBranchSwitcher';
import { 
  LayoutDashboard, 
  FileText, 
  ShoppingCart, 
  BarChart3, 
  Activity, 
  Settings, 
  LogOut,
  Building2,
  ChevronDown,
  ChevronRight,
  MinusCircle,
  RotateCcw
} from 'lucide-react';
import { TRN } from '../types';
import { STORAGE_KEYS } from '../constants';

interface ClientSidebarProps {
  user: User;
  currentView: ClientView;
  onViewChange: (view: ClientView) => void;
  onLogout: () => void;
  trns: TRN[];
  onTRNBranchSwitch: (trnId: string, branchId: string) => void;
}

export default function ClientSidebar({ 
  user, 
  currentView, 
  onViewChange, 
  onLogout,
  trns,
  onTRNBranchSwitch
}: ClientSidebarProps) {
  const [salesExpanded, setSalesExpanded] = useState(true);
  const [purchaseExpanded, setPurchaseExpanded] = useState(true);
  
  const selectedTRNId = localStorage.getItem(STORAGE_KEYS.SELECTED_TRN) || user.selectedTRN || '';
  const selectedBranchId = localStorage.getItem(STORAGE_KEYS.SELECTED_BRANCH) || user.selectedBranch || '';

  const salesMenuItems = [
    { id: 'sales-invoices' as ClientView, label: 'Invoices', icon: FileText },
    { id: 'sales-debit-notes' as ClientView, label: 'Debit Notes', icon: MinusCircle },
    { id: 'sales-credit-notes' as ClientView, label: 'Credit Notes', icon: RotateCcw },
  ];

  const purchaseMenuItems = [
    { id: 'purchase-invoices' as ClientView, label: 'Invoices', icon: FileText },
    { id: 'purchase-debit-notes' as ClientView, label: 'Debit Notes', icon: MinusCircle },
    { id: 'purchase-credit-notes' as ClientView, label: 'Credit Notes', icon: RotateCcw },
  ];

  const otherMenuItems = [
    { id: 'overview' as ClientView, label: 'Dashboard', icon: LayoutDashboard },
    { id: 'reports' as ClientView, label: 'Reports', icon: BarChart3 },
    { id: 'audit' as ClientView, label: 'Audit & Monitoring', icon: Activity },
    { id: 'settings' as ClientView, label: 'Settings', icon: Settings },
  ];

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Logo and Branding */}
      <div className="p-6 border-b border-gray-200 space-y-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-lg blur-sm opacity-50"></div>
            <div className="relative w-10 h-10 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-lg bg-gradient-to-r from-[#0052CC] to-indigo-600 bg-clip-text text-transparent">AIS Connect</h1>
            <p className="text-xs text-gray-500">UAE E-Invoicing</p>
          </div>
        </div>
        
        {/* TRN/Branch Switcher */}
        {selectedTRNId && selectedBranchId && (
          <TRNBranchSwitcher
            trns={trns}
            selectedTRNId={selectedTRNId}
            selectedBranchId={selectedBranchId}
            onSwitch={onTRNBranchSwitch}
          />
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {/* Dashboard - First */}
        <button
          onClick={() => onViewChange('overview')}
          className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
            currentView === 'overview'
              ? 'bg-gradient-to-r from-[#0052CC] to-indigo-600 text-white shadow-lg'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="text-sm">Dashboard</span>
        </button>

        {/* Sales Section */}
        <div className="pt-2">
          <button
            onClick={() => setSalesExpanded(!salesExpanded)}
            className="w-full flex items-center justify-between px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="text-sm">Sales</span>
            </div>
            {salesExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
          {salesExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
              {salesMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onViewChange(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all text-sm ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-600 -ml-2 pl-[10px]'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Purchase Section */}
        <div className="pt-2">
          <button
            onClick={() => setPurchaseExpanded(!purchaseExpanded)}
            className="w-full flex items-center justify-between px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
              <span className="text-sm">Purchase</span>
            </div>
            {purchaseExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
          {purchaseExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
              {purchaseMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onViewChange(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all text-sm ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-700 border-l-2 border-emerald-600 -ml-2 pl-[10px]'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Other Menu Items */}
        <div className="pt-2 space-y-1">
          {otherMenuItems.slice(1).map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-[#0052CC] to-indigo-600 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* User Profile and Logout */}
      <div className="p-4 border-t border-gray-200 space-y-3 bg-gradient-to-r from-gray-50 to-slate-50">
        <div className="px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-900 truncate">{user.name}</p>
          <p className="text-xs text-gray-500 truncate">{user.email}</p>
        </div>
        <Button
          onClick={onLogout}
          variant="outline"
          className="w-full flex items-center justify-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </Button>
      </div>
    </aside>
  );
}
